import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/account";

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(identifier, password);
      navigate(from, { replace: true });
    } catch (err) {
      // Distinguish "server said no" (has a response — wrong credentials,
      // locked account, etc.) from a real network failure (no response at
      // all — wrong host, CORS block, offline) instead of collapsing both
      // into the same generic invalid-credentials text.
      if (!err?.response) {
        setError("Unable to connect to the server. Please check your connection and try again.");
      } else {
        const msg = err.response.data?.detail;
        setError(typeof msg === "string" ? msg : "Invalid email or password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto px-4 py-16">
      <h1 className="text-xl font-semibold text-neutral-900 mb-6 text-center">Login</h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="login-identifier" className="block text-sm font-medium text-neutral-700 mb-1">Email or Name</label>
          <input
            id="login-identifier"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="login-password" className="block text-sm font-medium text-neutral-700">Password</label>
            <Link to="/forgot-password" className="text-xs text-neutral-500 underline hover:text-neutral-900">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 pl-3 pr-11 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-neutral-400 hover:text-neutral-700"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-2.5 hover:bg-neutral-800 disabled:opacity-50"
        >
          {submitting ? "Signing in..." : "Login"}
        </button>
      </form>

      <p className="text-sm text-neutral-500 text-center mt-6">
        Don&apos;t have an account?{" "}
        <Link to="/register" state={location.state} className="text-neutral-900 underline">
          Create one
        </Link>
      </p>
    </div>
  );
}
